import React, {useEffect, useMemo, useState} from 'react'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type MatrixProfileFieldKey =
  | 'watchkeeperOnShip'
  | 'nationality'
  | 'certificateOfCompetency'
  | 'issuingCountry'
  | 'administrationAcceptance'
  | 'tankerCertification'
  | 'specializedTankerTraining'
  | 'radioQualification'
  | 'operator'
  | 'manualRank'
  | 'tankersType'
  | 'allTypes'
  | 'totalYearsWatch'
  | 'monthsTours'
  | 'englishProficiency'

type MatrixProfileFieldDef = {
  key: MatrixProfileFieldKey
  label: string
  manualEntry?: boolean
  inputType?: 'text' | 'date'
  options?: string[]
}

const MATRIX_PROFILE_FIELDS: MatrixProfileFieldDef[] = [
  {key: 'watchkeeperOnShip', label: 'Watchkeeper on the Ship', options: ['Yes', 'No']},
  {key: 'nationality', label: 'Nationality'},
  {key: 'certificateOfCompetency', label: 'Certificate of Competency'},
  {key: 'issuingCountry', label: 'Issuing Country'},
  {key: 'administrationAcceptance', label: 'Admin Acceptance'},
  {key: 'tankerCertification', label: 'Tanker Certification'},
  {key: 'specializedTankerTraining', label: 'Specialized Tanker Training'},
  {key: 'radioQualification', label: 'Radio Qualification', manualEntry: true, inputType: 'text'},
  {key: 'operator', label: 'Operator', manualEntry: true, inputType: 'text'},
  {key: 'manualRank', label: 'Rank', manualEntry: true, inputType: 'text'},
  {key: 'tankersType', label: 'Tankers Type', manualEntry: true, inputType: 'text'},
  {key: 'allTypes', label: 'All Types', manualEntry: true, inputType: 'text'},
  {key: 'totalYearsWatch', label: 'Total Years Watch', manualEntry: true, inputType: 'text'},
  {key: 'monthsTours', label: 'Months Tours'},
  {key: 'englishProficiency', label: 'English Proficienc'},
]

const YEARS_IN_SERVICE_FIELD_KEYS: MatrixProfileFieldKey[] = [
  'radioQualification',
  'operator',
  'manualRank',
  'tankersType',
  'allTypes',
  'totalYearsWatch',
  'monthsTours',
  'englishProficiency',
]

const NON_YEARS_IN_SERVICE_FIELDS = MATRIX_PROFILE_FIELDS.filter(
  (field) => !YEARS_IN_SERVICE_FIELD_KEYS.includes(field.key)
)

const YEARS_IN_SERVICE_FIELDS = MATRIX_PROFILE_FIELDS.filter((field) =>
  YEARS_IN_SERVICE_FIELD_KEYS.includes(field.key)
)

const normalizeText = (value: any) => String(value ?? '').trim()

const normalizeRankToken = (value: any) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

const normalizeRankNoVowels = (value: any) =>
  normalizeRankToken(value).replace(/[aeiou]/g, '')

const getRankInitials = (value: any) =>
  normalizeText(value)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((token) => token[0])
    .join('')

const rankLooksEquivalent = (left: any, right: any) => {
  const a = normalizeRankToken(left)
  const b = normalizeRankToken(right)
  if (!a || !b) return false
  if (a === b || a.includes(b) || b.includes(a)) return true

  const aNoVowels = normalizeRankNoVowels(left)
  const bNoVowels = normalizeRankNoVowels(right)
  if (aNoVowels && bNoVowels && (aNoVowels === bNoVowels || aNoVowels.includes(bNoVowels) || bNoVowels.includes(aNoVowels))) {
    return true
  }

  const aInitials = getRankInitials(left)
  const bInitials = getRankInitials(right)
  if (aInitials && bInitials && (aInitials === bInitials || aInitials.includes(bInitials) || bInitials.includes(aInitials))) {
    return true
  }

  return false
}

const getNestedRecordValue = (record: any, path: string) => {
  return path.split('.').reduce<any>((acc, key) => {
    if (acc === null || acc === undefined) return undefined
    return acc[key]
  }, record)
}

const readFirstDefinedValue = (record: any, paths: string[]) => {
  for (const path of paths) {
    const value = getNestedRecordValue(record, path)
    if (value === null || value === undefined) continue
    if (typeof value === 'string' && value.trim() === '') continue
    return value
  }
  return undefined
}

const readFirstAvailableValue = (record: any, paths: string[]) => {
  const value = readFirstDefinedValue(record, paths)
  return value === undefined ? '-' : String(value)
}

const parseMonthsDaysToTotalDays = (value: any) => {
  const raw = normalizeText(value)
  if (!raw) return 0

  const monthMatch = raw.match(/(\d+)\s*(month\(s\)|months|month|mon|mth|m)/i)
  const dayMatch = raw.match(/(\d+)\s*(day\(s\)|days|day|d)/i)

  let months = monthMatch ? Number(monthMatch[1]) : 0
  let days = dayMatch ? Number(dayMatch[1]) : 0

  if (!monthMatch && !dayMatch) {
    const nums = (raw.match(/\d+/g) || []).map(Number)
    if (nums.length >= 2) {
      months = nums[0]
      days = nums[1]
    } else if (nums.length === 1) {
      months = nums[0]
    }
  }

  return months * 30 + days
}

const dateDiffInDaysInclusive = (fromDate?: string | null, toDate?: string | null) => {
  if (!fromDate || !toDate) return 0
  const from = new Date(fromDate)
  const to = new Date(toDate)
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) return 0
  return Math.floor((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000)) + 1
}

const formatMonthsDays = (totalDays: number) => {
  if (!totalDays || totalDays <= 0) return '-'
  const months = Math.floor(totalDays / 30)
  const days = totalDays % 30
  return `${months} Months ${days} Days`
}

const parseDateValue = (value: any): Date | null => {
  if (value === null || value === undefined) return null
  const parsed = new Date(String(value))
  if (Number.isNaN(parsed.getTime())) return null
  return parsed
}

const normalizeRankLabel = (value: any) =>
  String(value ?? '')
    .toUpperCase()
    .replace(/[._-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export const isCrewMatrixRankAllowed = (rankValue?: string | null) => {
  const rank = normalizeRankLabel(rankValue)
  if (!rank) return false

  const allowedTokens = [
    'MASTER',
    'CAPTAIN',
    'CHIEF OFFICER',
    'SECOND OFFICER',
    '2ND OFF',
    '2/O',
    'THIRD OFFICER',
    '3RD OFF',
    '3/O',
    'CHIEF ENGINEER',
    'SECOND ENGINEER',
    '2 ENG',
    '2/E',
    'THIRD ENGINEER',
    '3 ENG',
    '3/E',
    'FOURTH ENGINEER',
    '4 ENG',
    '4/E',
    'ELECTRICAL ENGINEER',
    'ELECTRICIAN / ETO',
    'ELECTRICIAN ETO',
    'ETO',
  ]

  return allowedTokens.some((token) => rank.includes(token))
}

const deriveMonthsTours = (record: any) => {
  const availabilityValue = readFirstDefinedValue(record, [
    'dateOfAvailability',
    'additionalInfo.availabilityDate',
    'availabilityDate',
  ])
  const explicitSignOnValue = readFirstDefinedValue(record, [
    'signOnDate',
    'activeSignOn.signOnDate',
    'activeSignOn.onboardFrom',
    'onboardFrom',
  ])

  const seaServiceRows = Array.isArray(record?.seaServiceHistory) ? record.seaServiceHistory : []
  let fallbackSignOn: Date | undefined
  for (const row of seaServiceRows as any[]) {
    const parsed = parseDateValue(row?.fromDate)
    if (!parsed) continue
    if (!fallbackSignOn || parsed.getTime() > fallbackSignOn.getTime()) {
      fallbackSignOn = parsed
    }
  }

  const signOnValue = explicitSignOnValue ?? fallbackSignOn?.toISOString()

  const availabilityDate = parseDateValue(availabilityValue)
  const signOnDate = parseDateValue(signOnValue)
  if (!availabilityDate || !signOnDate) return '-'
  if (availabilityDate < signOnDate) return '-'

  const days = Math.floor((availabilityDate.getTime() - signOnDate.getTime()) / (24 * 60 * 60 * 1000)) + 1
  if (days <= 0) return '-'

  const monthsRoundedToHalf = Math.round(((days / 30) * 2)) / 2
  if (monthsRoundedToHalf <= 0) return '0'

  return Number.isInteger(monthsRoundedToHalf)
    ? String(monthsRoundedToHalf)
    : monthsRoundedToHalf.toFixed(1)
}

const deriveEnglishProficiency = (record: any) => {
  const directValue = readFirstAvailableValue(record, ['englishProficiency'])
  if (directValue !== '-') return directValue

  const academicRows = Array.isArray(record?.academicQualifications)
    ? record.academicQualifications
    : Array.isArray(record?.academicBackgrounds)
    ? record.academicBackgrounds
    : []

  for (const row of academicRows as any[]) {
    const english = normalizeText(row?.englishProficiency)
    if (english) return english
  }

  return '-'
}

const deriveInRankExperience = (record: any, seaServiceRows?: any[]) => {
  const rows =
    Array.isArray(seaServiceRows) && seaServiceRows.length > 0
      ? seaServiceRows
      : Array.isArray(record?.seaServiceHistory)
      ? record.seaServiceHistory
      : []

  const selectedRank = readFirstAvailableValue(record, ['rankInfo.rank', 'rank.rank', 'rankName', 'rank'])
  const fallbackRank = rows.find((entry: any) => normalizeRankToken(entry?.rank))?.rank
  const targetRankRaw = selectedRank !== '-' ? selectedRank : fallbackRank

  const calculateTotalDays = (entries: any[], onlyForRank?: any) => {
    let totalDays = 0

    entries.forEach((entry: any) => {
      const entryRank = normalizeRankToken(entry?.rank)
      if (!entryRank) return

      if (onlyForRank && !rankLooksEquivalent(entry?.rank, onlyForRank)) {
        return
      }

      const fromTotalText = parseMonthsDaysToTotalDays(entry?.totalMonthsDays)
      if (fromTotalText > 0) {
        totalDays += fromTotalText
        return
      }

      totalDays += dateDiffInDaysInclusive(entry?.fromDate, entry?.toDate)
    })

    return totalDays
  }

  if (!targetRankRaw) {
    return formatMonthsDays(calculateTotalDays(rows))
  }

  let totalDays = calculateTotalDays(rows, targetRankRaw)

  // If no rank-matched rows were found, fallback to total service so value is not blank.
  if (totalDays <= 0) {
    totalDays = calculateTotalDays(rows)
  }

  return formatMonthsDays(totalDays)
}

const deriveCertificateOfCompetency = (record: any) => {
  const structured = readFirstAvailableValue(record, [
    'crewDocuments.cocGradeLevel',
    'cocGradeLevel',
    'documents.cocGradeLevel',
  ])
  if (structured !== '-') return structured

  const docs = Array.isArray(record?.documents) ? record.documents : []
  const cocDoc = docs.find((doc: any) => {
    const slot = String(doc?.slot ?? '').toUpperCase()
    const name = String(doc?.documentName ?? '').toLowerCase()
    return slot === 'COC' || name.includes('certificate of competency') || name.includes('coc')
  })
  if (!cocDoc) return '-'

  const grade = normalizeText(cocDoc?.gradeOrLevel ?? cocDoc?.gradeLevel ?? cocDoc?.level)
  return grade || '-'
}

const deriveIssuingCountry = (record: any) => {
  const issuing = readFirstAvailableValue(record, [
    'crewDocuments.cocIssuingAuthority',
    'cocIssuingAuthority',
    'documents.cocIssuingAuthority',
  ])
  return issuing
}

const boolToYesNo = (value: any) => {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  const normalized = normalizeText(value).toLowerCase()
  if (!normalized) return '-'
  if (['yes', 'y', 'true', '1'].includes(normalized)) return 'Yes'
  if (['no', 'n', 'false', '0'].includes(normalized)) return 'No'
  return '-'
}

type TankerRowConfig = {
  label: 'OIL' | 'CHEM' | 'GAS'
  slot: 'OIL_ENDORSEMENT' | 'CHEM_ENDORSEMENT' | 'GAS_ENDORSEMENT'
  fields: string[]
  gradePaths: string[]
}

const TANKER_ROW_CONFIGS: TankerRowConfig[] = [
  {
    label: 'OIL',
    slot: 'OIL_ENDORSEMENT',
    fields: [
      'crewDocuments.oilEndorsementNationality',
      'oilEndorsementNationality',
      'crewDocuments.oilEndorsementGradeLevel',
      'oilEndorsementGradeLevel',
      'crewDocuments.oilEndorsementNumber',
      'oilEndorsementNumber',
      'crewDocuments.oilEndorsementIssueDate',
      'oilEndorsementIssueDate',
      'crewDocuments.oilEndorsementPlaceOfIssue',
      'oilEndorsementPlaceOfIssue',
      'crewDocuments.oilEndorsementExpiryDate',
      'oilEndorsementExpiryDate',
    ],
    gradePaths: ['crewDocuments.oilEndorsementGradeLevel', 'oilEndorsementGradeLevel'],
  },
  {
    label: 'CHEM',
    slot: 'CHEM_ENDORSEMENT',
    fields: [
      'crewDocuments.chemEndorsementNationality',
      'chemEndorsementNationality',
      'crewDocuments.chemEndorsementGradeLevel',
      'chemEndorsementGradeLevel',
      'crewDocuments.chemEndorsementNumber',
      'chemEndorsementNumber',
      'crewDocuments.chemEndorsementIssueDate',
      'chemEndorsementIssueDate',
      'crewDocuments.chemEndorsementPlaceOfIssue',
      'chemEndorsementPlaceOfIssue',
      'crewDocuments.chemEndorsementExpiryDate',
      'chemEndorsementExpiryDate',
    ],
    gradePaths: ['crewDocuments.chemEndorsementGradeLevel', 'chemEndorsementGradeLevel'],
  },
  {
    label: 'GAS',
    slot: 'GAS_ENDORSEMENT',
    fields: [
      'crewDocuments.gasEndorsementNationality',
      'gasEndorsementNationality',
      'crewDocuments.gasEndorsementGradeLevel',
      'gasEndorsementGradeLevel',
      'crewDocuments.gasEndorsementNumber',
      'gasEndorsementNumber',
      'crewDocuments.gasEndorsementIssueDate',
      'gasEndorsementIssueDate',
      'crewDocuments.gasEndorsementPlaceOfIssue',
      'gasEndorsementPlaceOfIssue',
      'crewDocuments.gasEndorsementExpiryDate',
      'gasEndorsementExpiryDate',
    ],
    gradePaths: ['crewDocuments.gasEndorsementGradeLevel', 'gasEndorsementGradeLevel'],
  },
]

const hasTankerUpload = (record: any, slot: TankerRowConfig['slot']) => {
  const docs = Array.isArray(record?.documents) ? record.documents : []
  return docs.some((doc: any) => String(doc?.slot ?? '').toUpperCase() === slot)
}

const hasTankerRowData = (record: any, row: TankerRowConfig) => {
  const hasInlineValue = row.fields.some((path) => {
    const value = getNestedRecordValue(record, path)
    return value !== null && value !== undefined && String(value).trim() !== ''
  })

  return hasInlineValue || hasTankerUpload(record, row.slot)
}

const deriveTankerCertification = (record: any) => {
  const selectedRows = TANKER_ROW_CONFIGS.filter((row) => hasTankerRowData(record, row)).map((row) => row.label)
  return selectedRows.length ? selectedRows.join(', ') : '-'
}

const mapGradeLevelToTraining = (gradeLevel: any) => {
  const raw = normalizeText(gradeLevel).toUpperCase()
  if (!raw) return '-'

  if (raw.includes('ADV')) return 'Advance'
  if (raw.includes('BAS')) return 'Basic'
  if (raw.includes('II') || raw.includes('2')) return 'Advance'
  if (raw.includes('I') || raw.includes('1')) return 'Basic'

  return '-'
}

const deriveSpecializedTraining = (record: any) => {
  const trainings = TANKER_ROW_CONFIGS.filter((row) => hasTankerRowData(record, row))
    .map((row) => {
      const gradeLevel = readFirstAvailableValue(record, row.gradePaths)
      const trainingLevel = mapGradeLevelToTraining(gradeLevel)
      if (trainingLevel === '-') return null
      return `${row.label}: ${trainingLevel}`
    })
    .filter(Boolean) as string[]

  return trainings.length ? trainings.join(', ') : '-'
}

const deriveAdministrationAcceptance = (record: any) => {
  const explicit = readFirstDefinedValue(record, [
    'crewDocuments.cocAdminAcceptance',
    'cocAdminAcceptance',
    'documents.cocAdminAcceptance',
    'administrationAcceptance',
  ])

  if (explicit !== undefined) return boolToYesNo(explicit)
  return '-'
}

const deriveMatrixProfileFieldValue = (record: any, key: MatrixProfileFieldKey) => {
  switch (key) {
    case 'watchkeeperOnShip': {
      const raw = readFirstDefinedValue(record, [
        'watchkeeperOnShip',
        'watchKeeperOnShip',
        'isWatchkeeperOnShip',
      ])
      return boolToYesNo(raw)
    }
    case 'nationality':
      return readFirstAvailableValue(record, ['nationality'])
    case 'certificateOfCompetency':
      return deriveCertificateOfCompetency(record)
    case 'issuingCountry':
      return deriveIssuingCountry(record)
    case 'administrationAcceptance':
      return deriveAdministrationAcceptance(record)
    case 'tankerCertification':
      return deriveTankerCertification(record)
    case 'specializedTankerTraining':
      return deriveSpecializedTraining(record)
    case 'radioQualification':
      return readFirstAvailableValue(record, ['radioQualification'])
    case 'operator':
      return readFirstAvailableValue(record, ['operator'])
    case 'manualRank':
      return readFirstAvailableValue(record, ['manualRank'])
    case 'tankersType':
      return readFirstAvailableValue(record, ['tankersType'])
    case 'allTypes':
      return readFirstAvailableValue(record, ['allTypes'])
    case 'totalYearsWatch':
      return readFirstAvailableValue(record, ['totalYearsWatch'])
    case 'monthsTours':
      return deriveMonthsTours(record)
    case 'englishProficiency':
      return deriveEnglishProficiency(record)
    default:
      return '-'
  }
}

const getRankLabel = (record: any) =>
  readFirstAvailableValue(record, ['rankInfo.rank', 'rank.rank', 'rankName', 'rank'])

interface CrewMatrixProfileModalProps {
  isOpen?: boolean
  onClose?: () => void
  inline?: boolean
  readOnly?: boolean
  manualValues?: Partial<Record<string, string>>
  onManualValuesChange?: (values: Partial<Record<string, string>>) => void
  crewRecord: any | null
  seaServices?: any[]
  title?: string
}

const CrewMatrixProfileModal: React.FC<CrewMatrixProfileModalProps> = ({
  isOpen = false,
  onClose,
  inline = false,
  readOnly = false,
  manualValues,
  onManualValuesChange,
  crewRecord,
  seaServices = [],
  title = 'Crew Matrix',
}) => {
  const handleClose = onClose ?? (() => {})
  const [localManualValues, setLocalManualValues] = useState<Partial<Record<MatrixProfileFieldKey, string>>>({})
  const controlled = !!onManualValuesChange
  const currentManualValues =
    (manualValues as Partial<Record<MatrixProfileFieldKey, string>> | undefined) ?? localManualValues

  useEffect(() => {
    if (controlled) return
    if (!inline && !isOpen) return
    setLocalManualValues({})
  }, [controlled, inline, isOpen, crewRecord?.id, crewRecord?.crewId])

  const mergedRecord = useMemo(() => {
    if (!crewRecord) return null
    return {
      ...crewRecord,
      crewDocuments: {
        ...(crewRecord?.crewDocuments ?? {}),
        ...(crewRecord?.documents ?? {}),
      },
      courses: Array.isArray(crewRecord?.courses) ? crewRecord.courses : [],
    }
  }, [crewRecord])

  const rankLabel = useMemo(() => getRankLabel(mergedRecord), [mergedRecord])
  const inRankExperience = useMemo(() => deriveInRankExperience(mergedRecord, seaServices), [mergedRecord, seaServices])

  const getFieldValue = (key: MatrixProfileFieldKey) => {
    const manual = currentManualValues[key]
    if (manual !== undefined && manual !== null && String(manual).trim() !== '') return String(manual)
    return deriveMatrixProfileFieldValue(mergedRecord, key)
  }

  const setFieldValue = (key: MatrixProfileFieldKey, value: string) => {
    if (onManualValuesChange) {
      onManualValuesChange({
        ...currentManualValues,
        [key]: value,
      })
      return
    }

    setLocalManualValues((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  if (!inline && !isOpen) return null

  const content = mergedRecord ? (
    <div
      style={{
        border: '1px solid #d8dee6',
        borderRadius: '10px',
        overflow: 'hidden',
        background: '#f8fafc',
      }}
    >
      <div className='table-responsive' style={{overflowX: 'auto'}}>
        <table className='table align-middle mb-0' style={{minWidth: '2400px'}}>
          <thead>
            <tr>
              <th style={headerCellStyle} rowSpan={2}>Name</th>
              <th style={headerCellStyle} rowSpan={2}>Rank</th>
              <th style={headerCellStyle} rowSpan={2}>IN RANK EXPERIENCE</th>
              {NON_YEARS_IN_SERVICE_FIELDS.map((field) => (
                <th key={`matrix-head-${field.label}`} style={headerCellStyle} rowSpan={2}>
                  {field.label}
                </th>
              ))}
              <th
                style={{...headerCellStyle, textAlign: 'center'}}
                colSpan={YEARS_IN_SERVICE_FIELDS.length}
              >
                Years in service
              </th>
            </tr>
            <tr>
              {YEARS_IN_SERVICE_FIELDS.map((field) => (
                <th key={`matrix-sub-head-${field.label}`} style={headerCellStyle}>
                  {field.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={valueCellStyle}>{mergedRecord?.crewName || mergedRecord?.name || '-'}</td>
              <td style={valueCellStyle}>{rankLabel}</td>
              <td style={valueCellStyle}>{inRankExperience}</td>
              {MATRIX_PROFILE_FIELDS.map((field) => (
                <td key={`matrix-value-${field.label}`} style={valueCellStyle}>
                  {readOnly ? (
                    getFieldValue(field.key)
                  ) : field.options ? (
                    <SearchableSelect
                      className='form-select form-select-sm'
                      value={getFieldValue(field.key) === '-' ? '' : getFieldValue(field.key)}
                      onChange={(e) => setFieldValue(field.key, e.target.value)}
                    >
                      <option value=''>Select</option>
                      {field.options.map((option) => (
                        <option key={`${field.key}-${option}`} value={option}>
                          {option}
                        </option>
                      ))}
                    </SearchableSelect>
                  ) : field.manualEntry ? (
                    <input
                      type={field.inputType ?? 'text'}
                      className='form-control form-control-sm'
                      value={getFieldValue(field.key) === '-' ? '' : getFieldValue(field.key)}
                      onChange={(e) => setFieldValue(field.key, e.target.value)}
                      placeholder='Enter value'
                    />
                  ) : (
                    getFieldValue(field.key)
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  ) : (
    <div className='alert alert-warning mb-0'>No crew data available for Crew Matrix.</div>
  )

  if (inline) {
    return (
      <div className='col-12'>
        <div className='mb-3'>
          <h5 className='m-0'>{title}</h5>
        </div>
        {content}
      </div>
    )
  }

  return (
    <>
      <div className='modal fade show' style={{display: 'block'}} role='dialog' aria-modal='true' tabIndex={-1}>
        <div className='modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>{title}</h5>
              <button type='button' className='btn-close' onClick={handleClose} />
            </div>
            <div className='modal-body'>
              {content}
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-light' onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show' onClick={handleClose} />
    </>
  )
}

const headerCellStyle: React.CSSProperties = {
  background: '#e1e6ee',
  color: '#3f4a5a',
  fontSize: '12px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #d8dee6',
  padding: '12px 10px',
  minWidth: '170px',
}

const valueCellStyle: React.CSSProperties = {
  background: '#f8fafc',
  color: '#1f2937',
  fontSize: '14px',
  fontWeight: 700,
  borderBottom: 'none',
  borderRight: '1px solid #e5e7eb',
  padding: '12px 10px',
  minWidth: '170px',
  wordBreak: 'break-word',
}

export default CrewMatrixProfileModal
